import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import {
  rackLocationService,
  CreateRackLocationData,
} from "../../services/rack-location.service";
import { storeService, Store } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { showToast } from "../../lib/toast";

export default function CreateRackLocationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedStoreId } = useAuthStore();
  const [stores, setStores] = useState<Store[]>([]);
  const [formData, setFormData] = useState<CreateRackLocationData>({
    storeId: selectedStoreId || "",
    rackCode: "",
    rackName: "",
    zone: "",
    row: "",
    column: "",
    level: undefined,
    rackType: "",
    capacity: undefined,
    description: "",
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      setStores([]);
    }
  };

  const createMutation = useMutation({
    mutationFn: rackLocationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rack-locations"] });
      navigate("/rack-locations");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.storeId) {
      showToast.error("Store is required");
      return;
    }
    if (!formData.rackCode || !formData.rackName) {
      showToast.error("Rack code and name are required");
      return;
    }
    createMutation.mutate(formData);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold mb-6">Add Rack Location</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.storeId}
                onChange={(e) =>
                  setFormData({ ...formData, storeId: e.target.value })
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Store</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rack Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.rackCode}
                onChange={(e) =>
                  setFormData({ ...formData, rackCode: e.target.value })
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rack Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.rackName}
                onChange={(e) =>
                  setFormData({ ...formData, rackName: e.target.value })
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rack Type
              </label>
              <input
                type="text"
                value={formData.rackType || ""}
                onChange={(e) =>
                  setFormData({ ...formData, rackType: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Shelving, Bin"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zone
              </label>
              <input
                type="text"
                value={formData.zone || ""}
                onChange={(e) =>
                  setFormData({ ...formData, zone: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Row
              </label>
              <input
                type="text"
                value={formData.row || ""}
                onChange={(e) =>
                  setFormData({ ...formData, row: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Column
              </label>
              <input
                type="text"
                value={formData.column || ""}
                onChange={(e) =>
                  setFormData({ ...formData, column: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level
              </label>
              <input
                type="number"
                value={formData.level ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    level:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacity
              </label>
              <input
                type="number"
                value={formData.capacity ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {createMutation.isPending ? "Saving..." : "Save Location"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {createMutation.isError && (
            <div className="text-red-600 text-sm">
              Error creating rack location. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
